import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  FileText,
  FolderOpen,
  RefreshCw,
  Clock,
  Scale,
  Briefcase,
  ArrowUpRight,
  Calendar,
  Sparkles,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats, getRecentActivity, DashboardStats, RecentActivity } from '../../lib/api/dashboard';
import { formatDateLong, formatDateTime, formatNumber, formatTime } from '../../shared/utils/format';

const Dashboard: React.FC = () => {
  // Task 17: three boxes (studio, corpus, sources) + recent activity + the
  // three quick actions. No office/permission blocks: the server already
  // scopes "studio" to the caller's firm (every firm for the super admin).
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [statsData, activityData] = await Promise.all([
          getDashboardStats(),
          getRecentActivity(5),
        ]);
        if (!active) return;
        setStats(statsData);
        setRecentActivities(activityData.activities);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (!active) return;
        setError(t('dashboard.loadError'));
        setStats({
          firm: { documents: 0, bank_statements: 0, policies: 0, members: 0 },
          corpus: { legal_acts: { CH: 0, TI: 0, INT: 0 }, articles: 0, case_law_digests: 0, doctrine: 0,
                    federal_decisions: { BGE: 0, BGER: 0, BSTGER: 0, BVGER: 0 } },
          sources: { status: null, checked_at: null },
        });
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [t]);

  const greeting = (): string => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('dashboard.greeting.morning');
    if (hour < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  };

  const getActivityIcon = (type: RecentActivity['type']) => (type === 'bank_statement' ? Briefcase : FileText);

  const getActivityLabel = (type: RecentActivity['type']) =>
    t(type === 'bank_statement' ? 'lanes.bank_statements' : 'lanes.firm_documents');

  const formatTimeAgo = (timestamp: string | null): string => {
    if (!timestamp) return t('dashboard.relativeTime.unknown');

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return t('dashboard.relativeTime.now');
    if (diffInMinutes < 60) return t('dashboard.relativeTime.minutesAgo', { n: diffInMinutes });
    if (diffInHours < 24) return t('dashboard.relativeTime.hoursAgo', { n: diffInHours });
    if (diffInDays === 1) return t('dashboard.relativeTime.yesterday');
    if (diffInDays < 30) return t('dashboard.relativeTime.daysAgo', { n: diffInDays });
    return formatDateTime(date);
  };

  // The server aggregates every firm for the super admin (who belongs to
  // none): label the box accordingly instead of a firm picker.
  const firmBoxTitle = user?.firm ? t('dashboard.boxes.firm.title') : t('dashboard.boxes.firm.titleAll');

  const quickActions = [
    {
      title: t('dashboard.quickActions.chat.title'),
      description: t('dashboard.quickActions.chat.description'),
      icon: MessageSquare,
      link: '/chat',
    },
    {
      title: t('dashboard.quickActions.upload.title'),
      description: t('dashboard.quickActions.upload.description'),
      icon: FolderOpen,
      link: '/documents',
    },
    {
      title: t('dashboard.quickActions.legalActs.title'),
      description: t('dashboard.quickActions.legalActs.description'),
      icon: Scale,
      link: '/legal-acts',
    },
  ];

  const firmBoxStats = stats ? [
    { label: t('dashboard.boxes.firm.documents'), value: formatNumber(stats.firm.documents) },
    { label: t('dashboard.boxes.firm.bankStatements'), value: formatNumber(stats.firm.bank_statements) },
    { label: t('dashboard.boxes.firm.policies'), value: formatNumber(stats.firm.policies) },
    { label: t('dashboard.boxes.firm.members'), value: formatNumber(stats.firm.members) },
  ] : [];

  const corpusBoxStats = stats ? [
    { label: t('dashboard.boxes.corpus.legalActsCH'), value: formatNumber(stats.corpus.legal_acts.CH) },
    { label: t('dashboard.boxes.corpus.legalActsTI'), value: formatNumber(stats.corpus.legal_acts.TI) },
    { label: t('dashboard.boxes.corpus.legalActsINT'), value: formatNumber(stats.corpus.legal_acts.INT ?? 0) },
    { label: t('dashboard.boxes.corpus.articles'), value: formatNumber(stats.corpus.articles) },
    { label: t('dashboard.boxes.corpus.caseLawDigests'), value: formatNumber(stats.corpus.case_law_digests) },
    { label: t('dashboard.boxes.corpus.federalBGE'), value: formatNumber(stats.corpus.federal_decisions?.BGE ?? 0) },
    { label: t('dashboard.boxes.corpus.federalBGER'), value: formatNumber(stats.corpus.federal_decisions?.BGER ?? 0) },
    { label: t('dashboard.boxes.corpus.federalBSTGER'), value: formatNumber(stats.corpus.federal_decisions?.BSTGER ?? 0) },
    { label: t('dashboard.boxes.corpus.federalBVGER'), value: formatNumber(stats.corpus.federal_decisions?.BVGER ?? 0) },
    { label: t('dashboard.boxes.corpus.doctrine'), value: formatNumber(stats.corpus.doctrine) },
  ] : [];

  const sourcesStatusLabel = stats?.sources.status
    ? t(`dashboard.boxes.sources.statusValues.${stats.sources.status}`)
    : null;
  const sourcesCheckedLabel = stats?.sources.checked_at
    ? formatDateTime(stats.sources.checked_at)
    : t('dashboard.relativeTime.never');

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-primary-800 rounded-xl p-8 text-white"
      >
        <div className="flex justify-between items-start">
          <div>
            <h1 className="font-display text-4xl font-bold mb-2">
              {greeting()}, {user?.email?.split('@')[0] || t('dashboard.defaultUser')}!
            </h1>
            <p className="text-lg opacity-90">
              {t('dashboard.welcome')}
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm opacity-80">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDateLong(currentTime)}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatTime(currentTime)}
              </div>
            </div>
          </div>
          <Sparkles className="h-16 w-16 opacity-30" />
        </div>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Three boxes: studio, corpus, sources */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-card p-6 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-display text-lg font-bold mb-4">{firmBoxTitle}</h2>
            <dl className="grid grid-cols-2 gap-4">
              {firmBoxStats.map((stat) => (
                <div key={stat.label}>
                  <dt className="uppercase text-xs tracking-wider text-gray-500 mb-1">{stat.label}</dt>
                  <dd className="font-display text-xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-card p-6 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-display text-lg font-bold mb-4">{t('dashboard.boxes.corpus.title')}</h2>
            <dl className="grid grid-cols-2 gap-4">
              {corpusBoxStats.map((stat) => (
                <div key={stat.label}>
                  <dt className="uppercase text-xs tracking-wider text-gray-500 mb-1">{stat.label}</dt>
                  <dd className="font-display text-xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-card p-6 dark:bg-gray-900 dark:border-gray-800">
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              {t('dashboard.boxes.sources.title')}
            </h2>
            {sourcesStatusLabel ? (
              <dl className="space-y-3">
                <div>
                  <dt className="uppercase text-xs tracking-wider text-gray-500 mb-1">{t('dashboard.boxes.sources.status')}</dt>
                  <dd className="font-display text-xl font-bold text-gray-900 dark:text-gray-100">{sourcesStatusLabel}</dd>
                </div>
                <div>
                  <dt className="uppercase text-xs tracking-wider text-gray-500 mb-1">{t('dashboard.boxes.sources.checkedAt')}</dt>
                  <dd className="text-sm text-gray-700 dark:text-gray-300">{sourcesCheckedLabel}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">{t('dashboard.boxes.sources.noStatus')}</p>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-card p-6 dark:bg-gray-900 dark:border-gray-800">
        <h2 className="font-display text-xl font-bold mb-6 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('dashboard.recentActivity.title')}
        </h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : recentActivities.length > 0 ? (
          <div className="space-y-4">
            {recentActivities.map((activity, index) => {
              const Icon = getActivityIcon(activity.type);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                    <Icon className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{getActivityLabel(activity.type)}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{activity.title}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTimeAgo(activity.created_at)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">
            {t('dashboard.recentActivity.empty')}
          </p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quickActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={action.link}>
                <div className="group relative overflow-hidden rounded-xl bg-white border border-gray-200 shadow-card p-6 hover:border-gray-300 hover:shadow-md transition-all duration-200 dark:bg-gray-900 dark:border-gray-800 dark:hover:border-gray-700">
                  <div className="relative">
                    <div className="inline-flex p-3 rounded-lg bg-primary-100 dark:bg-primary-900/30 mb-4">
                      <Icon className="h-6 w-6 text-primary-700 dark:text-primary-300" />
                    </div>
                    <h3 className="font-display text-lg font-semibold mb-2 flex items-center gap-2">
                      {action.title}
                      <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{action.description}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
